import React, { useState, useMemo, useRef } from 'react';
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Space,
  Typography,
  Card,
  Divider,
  Tag,
  message,
  Input,
  DatePicker,
} from 'antd';
import {
  FileExcelOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import { formatCurrency } from '../maturity-schedule/MaturityTableUtils';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

interface CardMultiExportModalProps {
  visible: boolean;
  onClose: () => void;
  maturitySchedules: MaturityScheduleResponse[];
  cardInfo: {
    cardNumber: string;
    cardHolderName: string;
    customerName: string;
    bankName: string;
    cardType: string;
  };
  selectedYear: number;
}

const CardMultiExportModal: React.FC<CardMultiExportModalProps> = ({
  visible,
  onClose,
  maturitySchedules,
  cardInfo,
  selectedYear,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<any>(null);

  // Add table key to force re-render when modal opens
  const [tableKey, setTableKey] = useState(0);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<Record<string, any>>({});

  // Ref to track current filtered data
  const tableRef = useRef<any>(null);
  const [currentFilteredData, setCurrentFilteredData] = useState<
    MaturityScheduleResponse[]
  >([]);

  // Reset when modal opens
  React.useEffect(() => {
    if (visible) {
      setTableKey((prev) => prev + 1);
      setSelectedRowKeys([]);
      setFilteredInfo({});
      setSortedInfo({});
    }
  }, [visible]);

  // Filter out ABANDONED status schedules
  const validMaturitySchedules = useMemo(() => {
    return maturitySchedules.filter((record) => {
      return record.status !== 'ABANDONED';
    });
  }, [maturitySchedules]);

  // Update filtered data when valid maturity schedules change
  React.useEffect(() => {
    setCurrentFilteredData(validMaturitySchedules);
  }, [validMaturitySchedules]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'orange';
      case 'OVERDUE':
        return 'red';
      case 'PAID':
        return 'blue';
      case 'COMPLETED':
        return 'green';
      case 'ABANDONED':
        return 'gray';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Chưa làm';
      case 'OVERDUE':
        return 'Quá hạn';
      case 'PAID':
        return 'Đã nạp đáo';
      case 'COMPLETED':
        return 'Đã hoàn thành';
      case 'ABANDONED':
        return 'Không làm';
      default:
        return status;
    }
  };

  // Get date range for current year
  const currentYearRange = useMemo(() => {
    const startOfYear = dayjs().year(selectedYear).startOf('year');
    const endOfYear = dayjs().year(selectedYear).endOf('year');
    return [startOfYear, endOfYear];
  }, [selectedYear]);

  // Handle table change to track filtered data
  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);

    // Update current filtered data for all actions
    if (extra.currentDataSource) {
      setCurrentFilteredData(extra.currentDataSource);
    }
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setCurrentFilteredData(validMaturitySchedules);
    // Không xóa selectedRowKeys - giữ nguyên selections
    setTableKey((prev) => prev + 1); // Force table re-render
    message.success('Đã xóa tất cả bộ lọc');
  };

  // Search functionality for columns
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (
    dataIndex: string,
    placeholder: string
  ): ColumnType<MaturityScheduleResponse> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filteredValue: filteredInfo[dataIndex] || null,
    onFilter: (value, record) => {
      let searchValue = '';

      switch (dataIndex) {
        case 'repaymentAmount':
          searchValue = record.repaymentAmount.toString();
          break;
        case 'totalDeposit':
          searchValue = (record.totalDepositAmount || 0).toString();
          break;
        case 'totalWithdrawal':
          searchValue = (record.totalWithdrawalAmount || 0).toString();
          break;
        case 'remainingAmount':
          searchValue = (record.remainingAmount || 0).toString();
          break;
        case 'feeApplied':
          searchValue = (record.feeApplied || 0).toString();
          break;
        case 'commissionRateForPartner':
          searchValue = (record.commissionRateForPartner || 0).toString();
          break;
        default:
          searchValue = '';
      }

      return searchValue
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // Date filter for due date column
  const getDateFilterProps = (): ColumnType<MaturityScheduleResponse> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <RangePicker
          value={
            selectedKeys[0]
              ? JSON.parse(selectedKeys[0] as string).map((date: string) =>
                  dayjs(date)
                )
              : null
          }
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setSelectedKeys([
                JSON.stringify([
                  dates[0].format('YYYY-MM-DD'),
                  dates[1].format('YYYY-MM-DD'),
                ]),
              ]);
            } else {
              setSelectedKeys([]);
            }
          }}
          disabledDate={(current) => {
            const [startOfYear, endOfYear] = currentYearRange;
            return (
              current &&
              (current.isBefore(startOfYear, 'day') ||
                current.isAfter(endOfYear, 'day'))
            );
          }}
          style={{ marginBottom: 8, display: 'block' }}
          placeholder={['Từ ngày', 'Đến ngày']}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Lọc
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filteredValue: filteredInfo.dueDate || null,
    onFilter: (value, record) => {
      if (!value) return true;

      const [startDate, endDate] = JSON.parse(value as string);
      const recordDate = dayjs(record.dueDate);

      return (
        recordDate.isAfter(dayjs(startDate).subtract(1, 'day')) &&
        recordDate.isBefore(dayjs(endDate).add(1, 'day'))
      );
    },
  });

  // Calculate preview totals for selected records
  const previewData = useMemo(() => {
    const selectedRecords = validMaturitySchedules.filter((record) =>
      selectedRowKeys.includes(record.id)
    );

    const totals = selectedRecords.reduce(
      (acc, record) => {
        const deposit = record.totalDepositAmount || 0;
        const withdrawal = record.totalWithdrawalAmount || 0;
        const fee = (record.repaymentAmount * (record.feeApplied || 0)) / 100;
        const balance = record.balanceDifference || 0;
        const remaining = record.remainingAmount || 0;
        const payment = record.totalPaymentAmount || 0;

        return {
          count: acc.count + 1,
          totalRepayment: acc.totalRepayment + record.repaymentAmount,
          totalDeposit: acc.totalDeposit + deposit,
          totalWithdrawal: acc.totalWithdrawal + withdrawal,
          totalFee: acc.totalFee + fee,
          totalBalance: acc.totalBalance + balance,
          totalRemaining: acc.totalRemaining + remaining,
          totalPayment: acc.totalPayment + payment,
        };
      },
      {
        count: 0,
        totalRepayment: 0,
        totalDeposit: 0,
        totalWithdrawal: 0,
        totalFee: 0,
        totalBalance: 0,
        totalRemaining: 0,
        totalPayment: 0,
      }
    );

    return totals;
  }, [validMaturitySchedules, selectedRowKeys]);

  const columns: ColumnsType<MaturityScheduleResponse> = [
    {
      title: 'Chọn',
      key: 'select',
      width: 60,
      render: (_: any, record: MaturityScheduleResponse) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record.id]);
            } else {
              setSelectedRowKeys(
                selectedRowKeys.filter((key) => key !== record.id)
              );
            }
          }}
        />
      ),
    },
    {
      title: 'ID',
      key: 'id',
      dataIndex: 'id',
      width: 80,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span className="font-mono text-sm text-gray-600">#{record.id}</span>
      ),
    },
    {
      title: 'Hạn đáo',
      key: 'dueDate',
      dataIndex: 'dueDate',
      width: 120,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span>{dayjs(record.dueDate).format('DD/MM/YYYY')}</span>
      ),
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      ...getDateFilterProps(),
    },
    {
      title: 'Số tiền đáo hạn',
      key: 'repaymentAmount',
      dataIndex: 'repaymentAmount',
      width: 140,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(record.repaymentAmount)}
        </span>
      ),
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        a.repaymentAmount - b.repaymentAmount,
      ...getColumnSearchProps('repaymentAmount', 'Tìm theo số tiền...'),
    },
    {
      title: 'Đã nạp',
      key: 'totalDeposit',
      dataIndex: 'totalDeposit',
      width: 120,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(record.totalDepositAmount || 0)}
        </span>
      ),
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        (a.totalDepositAmount || 0) - (b.totalDepositAmount || 0),
      ...getColumnSearchProps('totalDeposit', 'Tìm theo số tiền đã nạp...'),
    },
    {
      title: 'Đã rút',
      key: 'totalWithdrawal',
      dataIndex: 'totalWithdrawal',
      width: 120,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span className="font-semibold text-orange-600">
          {formatCurrency(record.totalWithdrawalAmount || 0)}
        </span>
      ),
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        (a.totalWithdrawalAmount || 0) - (b.totalWithdrawalAmount || 0),
      ...getColumnSearchProps('totalWithdrawal', 'Tìm theo số tiền đã rút...'),
    },
    {
      title: 'Còn phải nạp',
      key: 'remainingAmount',
      dataIndex: 'remainingAmount',
      width: 130,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span className="font-semibold text-orange-600">
          {formatCurrency(record.remainingAmount || 0)}
        </span>
      ),
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        (a.remainingAmount || 0) - (b.remainingAmount || 0),
      ...getColumnSearchProps(
        'remainingAmount',
        'Tìm theo số tiền còn phải nạp...'
      ),
    },
    {
      title: 'Phí thu (%)',
      key: 'feeApplied',
      dataIndex: 'feeApplied',
      width: 100,
      render: (_: any, record: MaturityScheduleResponse) => (
        <span>{record.feeApplied ? `${record.feeApplied}%` : '-'}</span>
      ),
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        (a.feeApplied || 0) - (b.feeApplied || 0),
      ...getColumnSearchProps('feeApplied', 'Tìm theo tỷ lệ phí...'),
    },
    {
      title: 'Phí CTV (%)',
      key: 'commissionRateForPartner',
      dataIndex: 'commissionRateForPartner',
      width: 100,
      render: (_: any, record: MaturityScheduleResponse) => {
        const partnerFee = record.commissionRateForPartner || 0;

        return (
          <span className="font-medium text-purple-600">
            {partnerFee > 0 ? `${partnerFee}%` : '0%'}
          </span>
        );
      },
      sorter: (a: MaturityScheduleResponse, b: MaturityScheduleResponse) =>
        (a.commissionRateForPartner || 0) - (b.commissionRateForPartner || 0),
      ...getColumnSearchProps(
        'commissionRateForPartner',
        'Tìm theo phí CTV...'
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      dataIndex: 'status',
      width: 100,
      render: (_: any, record: MaturityScheduleResponse) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusText(record.status)}
        </Tag>
      ),
      filters: [
        { text: 'Chưa làm', value: 'PENDING' },
        { text: 'Quá hạn', value: 'OVERDUE' },
        { text: 'Đã nạp đáo', value: 'PAID' },
        { text: 'Đã hoàn thành', value: 'COMPLETED' },
      ],
      onFilter: (value: any, record: MaturityScheduleResponse) =>
        record.status === value,
      filteredValue: filteredInfo.status || null,
    },
  ];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Chỉ thêm những record trong filtered data mà chưa được chọn
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = Array.from(
        new Set([...selectedRowKeys, ...currentIds])
      );
      setSelectedRowKeys(newSelections);
    } else {
      // Chỉ bỏ chọn những record trong filtered data, giữ lại những record khác
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = selectedRowKeys.filter(
        (id) => !currentIds.includes(id)
      );
      setSelectedRowKeys(newSelections);
    }
  };

  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một kỳ đáo hạn để xuất Excel');
      return;
    }

    try {
      setLoading(true);
      const selectedRecords = validMaturitySchedules.filter((record) =>
        selectedRowKeys.includes(record.id)
      );

      // Import and use the card export service
      const { CardMaturityExportService } = await import(
        '../../utils/excel/cardMaturityExportService'
      );

      await CardMaturityExportService.exportToExcel({
        records: selectedRecords,
        cardInfo,
        month: 0, // Indicate cả năm
        year: selectedYear,
      });

      message.success(
        `Đã xuất Excel thành công cho ${selectedRecords.length} kỳ đáo hạn`
      );
      onClose();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedRowKeys([]);
    setSearchedColumn('');
    setTableKey((prev) => prev + 1);
    onClose();
  };

  const formatCardNumber = (cardNumber?: string) => {
    if (!cardNumber) return 'N/A';
    return `****${cardNumber.slice(-4)}`;
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <FileExcelOutlined className="text-green-600" />
          <span>Xuất Excel kỳ đáo hạn thẻ</span>
        </div>
      }
      open={visible}
      onCancel={handleModalClose}
      width={1400}
      destroyOnClose={true}
      footer={[
        <Button key="cancel" onClick={handleModalClose}>
          Hủy
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={loading}
          disabled={selectedRowKeys.length === 0}
        >
          Xuất Excel ({selectedRowKeys.length} kỳ đã chọn)
        </Button>,
      ]}
      className="card-multi-export-modal"
    >
      <div className="space-y-4">
        {/* Card Info */}
        <Card size="small" className="bg-blue-50 border-blue-200">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Text strong>Thông tin thẻ: </Text>
                <Tag color="blue" className="text-base">
                  {formatCardNumber(cardInfo.cardNumber)}
                </Tag>
              </div>
              <div>
                <Text strong>Kỳ: </Text>
                <Tag color="green">Năm {selectedYear}</Tag>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Text type="secondary">Chủ thẻ: </Text>
                <Text strong>{cardInfo.cardHolderName}</Text>
              </div>
              <div>
                <Text type="secondary">Khách hàng: </Text>
                <Text strong className="text-green-600">
                  {cardInfo.customerName}
                </Text>
              </div>
              <div>
                <Text type="secondary">Ngân hàng: </Text>
                <Text strong>{cardInfo.bankName}</Text>
              </div>
              <div>
                <Text type="secondary">Loại thẻ: </Text>
                <Text strong>{cardInfo.cardType}</Text>
              </div>
            </div>
          </div>
        </Card>

        {/* Search hint and controls */}
        <div className="flex items-center justify-between">
          <Space>
            <Text type="secondary" className="text-xs">
              💡 Mẹo: Click icon tìm kiếm ở header cột để lọc chi tiết
            </Text>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearAllFilters}
              type="default"
              size="small"
              className="text-red-500 hover:text-red-700 hover:border-red-300 border-red-200"
              title="Xóa tất cả bộ lọc"
            >
              <span className="text-xs">Xóa tất cả bộ lọc</span>
            </Button>
          </Space>
          <Text type="secondary">
            Hiển thị: {currentFilteredData.length}/
            {validMaturitySchedules.length} kỳ đáo hạn
          </Text>
        </div>

        {/* Selection Controls */}
        <div className="flex items-center justify-between">
          <Space>
            <Checkbox
              checked={
                currentFilteredData.length > 0 &&
                currentFilteredData.every((record) =>
                  selectedRowKeys.includes(record.id)
                )
              }
              indeterminate={
                selectedRowKeys.length > 0 &&
                currentFilteredData.some((record) =>
                  selectedRowKeys.includes(record.id)
                ) &&
                !currentFilteredData.every((record) =>
                  selectedRowKeys.includes(record.id)
                )
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Chọn hiển thị
            </Checkbox>
            <Text type="secondary">
              Đã chọn: {selectedRowKeys.length} kỳ | Hiển thị:{' '}
              {
                currentFilteredData.filter((record) =>
                  selectedRowKeys.includes(record.id)
                ).length
              }
              /{currentFilteredData.length}
            </Text>
          </Space>
          {Object.keys(filteredInfo).length > 0 && (
            <Tag color="orange" className="text-sm">
              Đang áp dụng bộ lọc
            </Tag>
          )}
        </div>

        {/* Table */}
        <Table
          ref={tableRef}
          columns={columns}
          dataSource={validMaturitySchedules}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} kỳ đáo hạn`,
          }}
          scroll={{ y: 400, x: 1200 }}
          size="small"
          className="border border-gray-200"
          key={tableKey}
          onChange={handleTableChange}
        />

        {/* Preview Summary */}
        {selectedRowKeys.length > 0 && (
          <>
            <Divider />
            <Card
              size="small"
              title={
                <div className="flex items-center space-x-2">
                  <EyeOutlined />
                  <span>Xem trước tổng hợp</span>
                </div>
              }
              className="bg-green-50 border-green-200"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Text type="secondary">Số kỳ đáo hạn:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {previewData.count} kỳ
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng tiền đáo hạn:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {formatCurrency(previewData.totalRepayment)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng đã nạp:</Text>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(previewData.totalDeposit)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng đã rút:</Text>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatCurrency(previewData.totalWithdrawal)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalFee)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Còn phải nạp:</Text>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatCurrency(previewData.totalRemaining)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Thanh toán:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalPayment)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Chênh lệch:</Text>
                  <div
                    className={`text-lg font-semibold ${previewData.totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(previewData.totalBalance)}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CardMultiExportModal;
